/* eslint-disable no-underscore-dangle */
import {
    useNavigation,
    useNavigationState
} from '@react-navigation/native';
import {
    useEffect,
    useRef
} from 'react';
import { useSelector } from 'react-redux';

import {
    BigButton,
    NavigationList,
    Page,
    React,
    Section,
    TopNotification,
    View
} from '../../common';
import { getEnums } from '../../i18n';
import {
    getFavoritesFull,
    selectMedCenterDetails
} from '../../selectors';
import { useLiveFirebaseItem } from '../../services/firebase';
import {
    putIntoStore,
    SCHEME
} from '../../store';
import { COLORS } from '../../styles/colors';
import { getRouteHistory } from '../../utils/getRouteHistory';
import { updateFavPhotoUrlAndName } from '../Doctors/DoctorDetails';
import { resetDoctorsFilters } from '../Doctors/utils';
import {
    McBannerMain,
    McDetailedInfoColumns
} from './snippets';

const useNavTo = (clinic) => {
    const navigation = useNavigation();
    return React.useCallback(
        (route) => {
            putIntoStore('clinicFilterReset', true);

            if (route.id === 'DoctorList') {
                resetDoctorsFilters(
                    {
                        specialty: '',
                        worksAt: [ clinic._id ],
                        profiles: '0',
                    },
                    undefined,
                    route?.from ?? 'MedCenterDetails',
                );
            } else if (route.id === 'MedCenterServices') {
                putIntoStore(SCHEME.UMS_FILTER, {
                    medcenters: [ clinic._id ],
                    withPrograms: false,
                });
                navigation.navigate('MedCenterServices', {
                    mdInfo: clinic,
                    from: route?.from,
                });
                return;
            }
            const fromRouter = {
                MedCenterDetails: 'Clinic',
                default: '',
                Visit: 'Visit',
            };
            const fromArg = fromRouter[route?.from ?? 'default'] ?? '';
            const to = route.id === 'DoctorList' ? `DoctorsSpecialtiesSearch${fromArg}` : route.id || route;
            navigation.navigate(to, {
                mdInfo: clinic,
                from: route?.from ?? 'MedCenterDetails',
            });
        },
        [ clinic ],
    );
};

const useController = ({
    _id: _clinicId,
    id: clinicId,
    branchDoctorsCounter,
}) => {
    const {
        clinic,
        hasServices,
        isLoading,
    } = useSelector((state) =>
        selectMedCenterDetails(state, _clinicId ?? clinicId),
    );
    const feedbackSum = useLiveFirebaseItem('clinics_feedback_sum', clinicId);
    const doctorsCounter = branchDoctorsCounter || clinic.branchDoctorsCounter;

    const hasDoctors = !!doctorsCounter || doctorsCounter === 0 ? doctorsCounter > 0 : true;
    const filtredFields = [ hasServices ? null : 'MedCenterServices',
        hasDoctors ? null : 'DoctorList' ];

    return [
        feedbackSum
            ? Object.assign(Object.create(clinic), {
                textFeedbackCount: feedbackSum.textFeedbackCount || 0,
                feedbackCount: feedbackSum.feedbackCount || 0,
            })
            : clinic,
        useNavTo(clinic),
        filtredFields,
        isLoading,
    ];
};

/**
 * MedCenter Details Page(☆☆☆☆☆).
 */
export const MedCenterDetails = Page.register(
    ({
        mdInfo = {},
        navigation,
        byOnlineBooking = false,
        from = 'MedCenterDetails',
    }) => {
        const [ clinic,
            navTo,
            filtredFields,
            isLoading ] = useController(mdInfo);
        const favorites = useSelector(getFavoritesFull);
        const {
            connectivityStatus,
            notification: notificationSrc,
        } = clinic;
        const notification = { ...(notificationSrc ?? {}) };
        const routes = getRouteHistory(useNavigationState((s) => s));
        React.useEffect(() => {
            if (!mdInfo?.name && clinic?.name) {
                navigation.setParams({ mdInfo: clinic });
            }
        }, [ clinic ]);

        const isFavUpdated = useRef(false);

        useEffect(() => {
            const favFound = favorites?.find?.((fav) => fav?.id === clinic?.id);
            if (favFound) {
                const name = clinic?.name?.replace(' ', '\n');
                if (favFound?.photoUrl !== clinic?.photoUrl || favFound?.dispalyName !== name) {
                    isFavUpdated?.current === false &&
                    updateFavPhotoUrlAndName(favorites, favFound, clinic?.photoUrl, name);
                    isFavUpdated.current = true;
                }
            }
        }, [ favorites,
            clinic?.id,
            clinic?.photoUrl,
            clinic?.name ]);

        if (!clinic || !clinic?.id) {
            return (
                <Page
                    hint="titles.mcNotFound"
                    isLoading={isLoading}
                />
            );
        }

        const offlineMessages = getEnums('сlinicOfflineWarning')
            ?.find(({ id }) => id === clinic?.id)?.routeTo;
        if (notification?.message && offlineMessages) {
            notification.message = offlineMessages;
            notification.collapsible = 5;
        }
        return (
            <Page
                name="medcenter-details"
                hint={
                    (typeof notification === 'object' && Object.keys(notification)?.length > 0) ||
                    typeof notification !== 'object'
                        ? notification
                        : undefined
                }
                isLoading={isLoading}
            >
                {connectivityStatus !== 'online' ? null : (
                    <TopNotification
                        hint={{
                            level: 'success',
                            message: 'hint.online_booking_is_available',
                        }}
                    />
                )}
                {connectivityStatus !== 'unavailable' ? null : (
                    <TopNotification hint="hint.online_booking_is_not_available"/>
                )}
                <Section
                    style={{
                        paddingTop: connectivityStatus === 'online' || connectivityStatus === 'unavailable' ? 20 : 0,
                        paddingBottom: 16,
                        backgroundColor: 'white',
                        marginTop: 0,
                        borderColor: COLORS.BORDER_GRAY,
                        borderBottomWidth: 1,
                    }}
                >
                    <McBannerMain clinic={clinic}/>
                    {(connectivityStatus === 'unavailable' && mdInfo?.onlineConnectivity) ||
                    !mdInfo?.onlineConnectivity ||
                    byOnlineBooking ? (
                            <BigButton
                                title="buttons.make_appointment_medcenter"
                                trackingId={byOnlineBooking ? `ai_btn_mc_booking_from_ums` : 'ai_btn_mc_from_ums'}
                                trackingParams={{
                                    clinicId: clinic._id,
                                    journey: routes,
                                }}
                                action={
                                    byOnlineBooking &&
                                !(
                                    (connectivityStatus === 'unavailable' && mdInfo?.onlineConnectivity) ||
                                    !mdInfo?.onlineConnectivity
                                )
                                        ? () => {
                                            navigation.navigate('MedCenterRecordFull', {
                                                onPress: (f) => {
                                                    navTo({
                                                        id: 'DoctorList',
                                                        from: f ?? from ?? 'MedCenterDetails',
                                                    });
                                                },
                                                mdInfo: clinic,
                                                byOnlineBooking,
                                            });
                                        }
                                        : () =>
                                            navigation.navigate('MedCenterRecord', {
                                                mdInfo: clinic,
                                                byOnlineBooking,
                                                from: from ?? 'MedCenterDetails',
                                            })
                                }
                            />
                        ) : (
                            <BigButton
                                title="buttons.choose_doctor"
                                action={() =>
                                    navTo({
                                        id: 'DoctorList',
                                        from: from ?? 'MedCenterDetails',
                                    })
                                }
                            />
                        )}
                </Section>
                <View>
                    <McDetailedInfoColumns
                        clinic={clinic}
                        navTo={navTo}
                    />
                </View>
                <NavigationList
                    dataEnum="detailsNavigation"
                    onItem={(params) => {
                        params.from = from ?? 'MedCenterDetails';
                        navTo(params);
                    }}
                    filtredFields={filtredFields}
                />
            </Page>
        );
    },
);
